/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Container } from '@components/atoms/Container';
import { Space } from '@components/atoms/Space';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useUpdateUser } from '@services/useUpdateUser';
import { Form } from '@components/atoms/Form';
import { InputErrors } from '@types';
import { REGEX_IS_EMAIL, SITE_URLS } from '@constants';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useNotifications } from '@hooks/useNotifications';
import { convertFilesToBase64 } from '@utils/image';
import { PopupConfirm } from '../PopupConfirm';
import { useData } from '@hooks/useData';
import { useUpdateLogin } from '@services/useUpdateLogin';
import { ErrorMessages } from '@components/atoms/ErrorMessages';

export interface MyPersonalDataProps {
    personalData: {
        firstName: string;
        lastName: string;
        photo: string;
        email: string;
        phone: string;
        over18: boolean;
        fiscalNumber: string;
        provider: string;
        has2FA: boolean;
        allowGeolocationTracking: boolean;
    };
}

interface EditUserFormErrors {
    firstName: InputErrors;
    lastName: InputErrors;
    photo: InputErrors;
    email: string[];
    oldPassword: string[];
    password: 'valid' | 'invalid' | null;
    passwordConfirm: string[];
    fiscalNumber: InputErrors;
    mobileNumber: InputErrors;
    over18: InputErrors;
}

export const MyPersonalData: FC<MyPersonalDataProps> = ({ personalData }) => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const { navigate } = useCustomNavigate();
    const { request: requestUpdateUser, loading, error: errorUpdateUser, lastResponseTime } = useUpdateUser();
    const { request: requestUpdateLogin } = useUpdateLogin();

    const formRef = useRef<HTMLFormElement>(null);
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [photo, setPhoto] = useState<string>('');
    const [changePassword, setChangePassword] = useState<boolean>(false);
    const [oldPassword, setOldPassword] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setConfirmPassword] = useState<string>('');
    const [validPassword, setValidPassword] = useState<boolean>(false);
    const [fiscalNumber, setFiscalNumber] = useState<string>('');
    const [mobileNumber, setMobileNumber] = useState<string>('');
    const [over18, setOver18] = useState<boolean>(false);
    const [provider, setProvider] = useState<string>('');
    const [has2FA, setHas2FA] = useState<boolean>(false);
    const [allowGeolocationTracking, setAllowGeolocationTracking] = useState<boolean>(false);
    const [newUserPhotoPreview, setNewUserPhotoPreview] = useState<string | ArrayBuffer | null>(null);
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);

    const translations = {
        title: t('my-profile-component.personal-data'),
        firstName: t('my-profile-component.my-account.firstName.label'),
        firstNameRequiredError: t('my-profile-component.my-account.firstName.required-error'),
        lastName: t('my-profile-component.my-account.lastName.label'),
        lastNameRequiredError: t('my-profile-component.my-account.lastName.required-error'),
        photoAlt: t('my-profile-component.my-account.photo.alt'),
        email: t('my-profile-component.my-account.email.label'),
        emailRequiredError: t('my-profile-component.my-account.email.required-error'),
        emailInvalidError: t('my-profile-component.my-account.email.invalid-error'),
        password: {
            changePwd: t('my-profile-component.my-account.password.change-pwd.label'),
            currentPwd: t('my-profile-component.my-account.password.current-pwd.label'),
            currentPwdRequiredError: t('my-profile-component.my-account.password.current-pwd.required-error'),
            newPwd: t('my-profile-component.my-account.password.new-pwd.label'),
            newPwdRequiredError: t('my-profile-component.my-account.password.new-pwd.required-error'),
            confirmNewPwd: t('my-profile-component.my-account.password.confirm-new-pwd.label'),
            confirmNewPwdMatchingError: t('my-profile-component.my-account.password.confirm-new-pwd.match-error'),
        },
        twoFactorAuth: t('my-profile-component.my-account.two-factor-auth.title'),
        twoFactorAuthConfirm: t('my-profile-component.my-account.two-factor-auth.confirm'),
        twoFactorAuthCancelButtonText: t('my-profile-component.my-account.two-factor-auth.cancel-button'),
        twoFactorAuthConfirmButtonText: t('my-profile-component.my-account.two-factor-auth.confirm-button'),
        trackLocation: t('my-profile-component.my-account.track-location'),
        mobileNumber: t('my-profile-component.my-account.mobile.label'),
        mobileNumberRequiredError: t('my-profile-component.my-account.mobile.required-error'),
        over18Label: t('my-profile-component.my-account.over-18.label'),
        fiscalNumber: t('my-profile-component.my-account.fiscalNumber.label'),
        fiscalNumberRequiredError: t('my-profile-component.my-account.fiscalNumber.required-error'),
        saveBtn: t('my-profile-component.save-btn'),
        select: t('my-profile-component.select'),
        success: t('my-profile-component.success'),
    };

    const submitForm: () => Promise<void> = async () => {
        const passwordToSubmit = provider === 'google' ? '' : oldPassword;

        const userPhotoInput = document.getElementById('user-photo') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64userImage: any = userPhotoInput?.files ? await convertFilesToBase64(userPhotoInput.files) : null;

        requestUpdateUser({
            first_name: firstName,
            last_name: lastName,
            photo_img: base64userImage?.[0] || undefined,
            email: email,
            old_password: passwordToSubmit,
            password: password,
            password_confirmation: passwordConfirm,
            fiscal_number: fiscalNumber,
            phone: mobileNumber,
            over_18: over18,
            provider: !firstName.trim() ? 'google' : 'normal',
            has2FA: has2FA,
            allow_geolocation_tracking: allowGeolocationTracking,
        });
    };

    const [errors, setErrors] = useState<EditUserFormErrors>({
        firstName: [],
        lastName: [],
        photo: [],
        email: [],
        password: null,
        oldPassword: [],
        passwordConfirm: [],
        mobileNumber: [],
        over18: [],
        fiscalNumber: [],
    });

    const handleOnClickUpdateUser = () => {
        const errors = validateForm();

        const isPasswordValid = !changePassword || errors.password === 'valid';
        const isOldPasswordValid = provider === 'google' || (!changePassword ? true : oldPassword.trim().length > 0);

        if (
            errors.firstName.length === 0 &&
            errors.lastName.length === 0 &&
            errors.mobileNumber.length === 0 &&
            errors.over18.length === 0 &&
            errors.email.length === 0 &&
            isPasswordValid &&
            isOldPasswordValid &&
            errors.passwordConfirm.length === 0
        ) {
            if (!has2FA && personalData.has2FA) {
                setShowConfirmPopup(true);
                return;
            }
            formRef?.current?.requestSubmit();
        }
    };

    const handleConfirmDisable2FA = () => {
        setShowConfirmPopup(false);
        formRef?.current?.requestSubmit();
    };

    const validateForm: () => EditUserFormErrors = () => {
        const newErrors: EditUserFormErrors = {
            firstName: [],
            lastName: [],
            photo: [],
            email: [],
            oldPassword: [],
            password: changePassword ? (validPassword ? 'valid' : 'invalid') : null,
            passwordConfirm: [],
            fiscalNumber: [],
            mobileNumber: [],
            over18: [],
        };

        !firstName.trim() && newErrors.firstName.push(translations.firstNameRequiredError);
        !lastName.trim() && newErrors.lastName.push(translations.lastNameRequiredError);
        !mobileNumber.trim() && newErrors.mobileNumber.push(translations.mobileNumberRequiredError);

        // !fiscalNumber.trim() && newErrors.fiscalNumber.push(translations.fiscalNumberRequiredError);

        if (!email.trim()) {
            newErrors.email.push(translations.emailRequiredError);
        } else {
            !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        }

        if (changePassword) {
            if (provider !== 'google' && !oldPassword.trim()) {
                newErrors.oldPassword.push(translations.password.currentPwdRequiredError);
            }

            newErrors.passwordConfirm.length === 0 &&
                password !== passwordConfirm &&
                newErrors.passwordConfirm.push(translations.password.confirmNewPwdMatchingError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    useEffect(() => {
        setFirstName(personalData.firstName || '');
        setLastName(personalData.lastName || '');
        setPhoto(personalData.photo || '');
        setEmail(personalData.email || '');
        setFiscalNumber(personalData.fiscalNumber || '');
        setMobileNumber(personalData.phone || '');
        setOver18(personalData.over18 || false);
        setProvider(personalData.provider || '');
        setHas2FA(personalData.has2FA);
        setAllowGeolocationTracking(personalData.allowGeolocationTracking);
    }, [
        personalData.photo,
        personalData.email,
        personalData.firstName,
        personalData.fiscalNumber,
        personalData.lastName,
        personalData.phone,
        personalData.over18,
        personalData.provider,
        personalData.has2FA,
        personalData.allowGeolocationTracking,
    ]);

    useEffect(() => {
        if (lastResponseTime) {
            if (!errorUpdateUser) {
                showDefaultNotification({ type: 'success', message: translations.success });
                requestUpdateLogin();

                setFirstName(firstName);
                setLastName(lastName);
                setEmail(photo);
                setEmail(email);
                setFiscalNumber(fiscalNumber);
                setMobileNumber(mobileNumber);
                setOver18(over18);
                setProvider(provider);
                setHas2FA(has2FA);
                setAllowGeolocationTracking(allowGeolocationTracking);

                setPassword('');
                setConfirmPassword('');
                setOldPassword('');
                setChangePassword(false);
            } else {
                showDefaultNotification({ type: 'error', message: errorUpdateUser });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    useEffect(() => {
        if (lastResponseTime && has2FA && has2FA !== personalData.has2FA && !errorUpdateUser) {
            navigate(`/${SITE_URLS.twoFactorAuthActivate}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    // update preview of new user image
    useEffect(() => {
        const logoInput = document.getElementById('user-photo') as HTMLInputElement;
        const file = logoInput?.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = () => setNewUserPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setNewUserPhotoPreview(null);
        }
    }, [photo]);

    return (
        <Container>
            <div data-testid="my-profile">
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                <Form formRef={formRef} onSubmit={() => submitForm()}>
                    {/* firstName */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.text}
                            label={translations.firstName}
                            variant={InputVariants.labeledInput}
                            value={firstName}
                            onChange={(value) => setFirstName(value)}
                            state={errors.firstName.length > 0 ? InputStates.error : undefined}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* lastName */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.text}
                            label={translations.lastName}
                            variant={InputVariants.labeledInput}
                            value={lastName}
                            onChange={(value) => setLastName(value)}
                            state={errors.lastName.length > 0 ? InputStates.error : undefined}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* email */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.email}
                            label={translations.email}
                            variant={InputVariants.labeledInput}
                            value={email}
                            onChange={(value) => setEmail(value)}
                            state={errors.email.length > 0 ? InputStates.error : undefined}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* change password */}
                    <Checkbox
                        type={CheckboxType.normal}
                        name="change-password"
                        value="true"
                        checked={changePassword}
                        onChange={() => setChangePassword((prev) => !prev)}
                        label={
                            <Typography fontSize="12">
                                <>{`${translations.password.changePwd} `}</>
                            </Typography>
                        }
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* oldPassword */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.password}
                            variant={InputVariants.labeledInput}
                            label={`${translations.password.currentPwd}*`}
                            value={oldPassword}
                            onChange={(value) => {
                                if (provider !== 'google') {
                                    setOldPassword(value);
                                }
                            }}
                            state={errors.oldPassword.length > 0 && provider !== 'google' ? InputStates.error : undefined}
                            errorMessages={provider !== 'google' ? errors.oldPassword : []}
                            disabled={provider === 'google' || !changePassword}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* password */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.password}
                            variant={InputVariants.labeledInput}
                            label={`${translations.password.newPwd}*`}
                            value={password}
                            onChange={(value) => setPassword(value)}
                            state={errors.password === 'invalid' ? InputStates.error : undefined}
                            checkRules={InputCheckRules.passwordBuild}
                            onValidationChange={setValidPassword}
                            disabled={!changePassword}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* confPassword */}
                    <div className="col-md-6">
                        <Input
                            type={InputTypes.password}
                            variant={InputVariants.labeledInput}
                            label={`${translations.password.confirmNewPwd}*`}
                            value={passwordConfirm}
                            onChange={(value) => setConfirmPassword(value)}
                            state={errors.passwordConfirm.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.passwordConfirm}
                            disabled={!changePassword}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    {/* 2af checkbox */}
                    <div className="col-md-6">
                        <Checkbox
                            type={CheckboxType.normal}
                            name="2fa"
                            value="on"
                            id="2fa"
                            checked={has2FA}
                            onChange={() => setHas2FA(!has2FA)}
                            label={
                                <Typography color="muted" fontSize="12">
                                    {`${translations.twoFactorAuth} `}
                                </Typography>
                            }
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <TrackLocationCheckbox
                        allowGeolocationTracking={allowGeolocationTracking}
                        setAllowGeolocationTracking={(value) => setAllowGeolocationTracking(value)}
                    />
                    <Typography appearanceLevel={TypographyAppearanceVariants.level7} tagVariant={TypographyTagVariants.h2}>
                        {translations.title}
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                    {/* fiscalNumber */}
                    <div className="col-md-6 mb-3">
                        <Input
                            type={InputTypes.text}
                            label={translations.fiscalNumber}
                            variant={InputVariants.labeledInput}
                            value={fiscalNumber}
                            onChange={(value) => setFiscalNumber(value)}
                            // state={errors.fiscalNumber.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.fiscalNumber}
                        />
                    </div>
                    {/* mobileNumber */}
                    <div className="col-md-6 mb-3">
                        <Input
                            type={InputTypes.tel}
                            label={translations.mobileNumber}
                            variant={InputVariants.labeledInput}
                            value={mobileNumber}
                            onChange={(value) => setMobileNumber(value)}
                            state={errors.mobileNumber.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.mobileNumber}
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <Checkbox
                            type={CheckboxType.normal}
                            name="over-18"
                            value="true"
                            checked={over18}
                            onChange={() => setOver18(!over18)}
                            label={
                                <Typography color="muted" fontSize="12">
                                    {translations.over18Label}
                                </Typography>
                            }
                        />
                        {errors.over18.length > 0 && <ErrorMessages messages={errors.over18} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <div className="col-md-6">
                        <Input
                            id="user-photo"
                            type={InputTypes.file}
                            label={t('my-profile-component.my-account.photo.label')}
                            variant={InputVariants.labeledInput}
                            value={photo || ''}
                            onChange={(value) => setPhoto(value)}
                            state={errors.photo.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.photo}
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <div>
                        {photo && (
                            <img
                                src={(newUserPhotoPreview as string) || photo || ''}
                                alt={translations.photoAlt}
                                style={{ width: '100px', height: '100px', borderRadius: '50%' }}
                            />
                        )}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    <Button
                        label={translations.saveBtn}
                        mode={ButtonMode.primary}
                        onClick={() => handleOnClickUpdateUser()}
                        disabled={loading}
                    />
                </Form>
                <Space spaceBreakpoints={{ xs: { space: 50 } }} />
            </div>
            <PopupConfirm
                show={showConfirmPopup}
                onHide={() => setShowConfirmPopup(false)}
                question={translations.twoFactorAuth}
                confirmation={translations.twoFactorAuthConfirm}
                cancelButtonText={translations.twoFactorAuthCancelButtonText}
                confirmButtonText={translations.twoFactorAuthConfirmButtonText}
                onClickConfirm={handleConfirmDisable2FA}
                onClickCancel={() => setShowConfirmPopup(false)}
            />
        </Container>
    );
};

export interface TrackLocationCheckboxProps {
    allowGeolocationTracking: boolean;
    setAllowGeolocationTracking: (value: boolean) => void;
}

const TrackLocationCheckbox: FC<TrackLocationCheckboxProps> = ({ allowGeolocationTracking, setAllowGeolocationTracking }) => {
    const { allSettings } = useData();
    const { t } = useTranslation();

    const translations = {
        trackLocation: t('my-profile-component.my-account.track-location'),
    };

    return (
        <>
            {allSettings?.data?.module_beacons?.active && (
                <>
                    <div className="col-md-6">
                        <Checkbox
                            type={CheckboxType.normal}
                            name="track-location"
                            value="track-location"
                            id="track-location"
                            checked={allowGeolocationTracking}
                            onChange={() => setAllowGeolocationTracking(!allowGeolocationTracking)}
                            label={
                                <Typography color="muted" fontSize="12">
                                    {`${translations.trackLocation}`}
                                </Typography>
                            }
                        />
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </>
            )}
        </>
    );
};
